import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Mail, Phone, MapPin, Linkedin, CheckCircle } from 'lucide-react';
import { mockData } from '../mock';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [organization, setOrganization] = useState(mockData.organization);
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    fetchOrganization();
  }, []);

  const fetchOrganization = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/organization`);
      if (response.data) {
        setOrganization({
          ...mockData.organization,
          ...response.data
        });
      }
    } catch (error) {
      console.error('Failed to fetch organization:', error);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setIsSubscribing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/submissions/newsletter`, { email });
      setSubscribed(true);
      setEmail('');
      toast.success('Thank you for subscribing to our newsletter!');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Organization Info */}
          <div className="space-y-4">
            <div>
              <h3 className="text-white font-bold text-2xl mb-2">{organization.name}</h3>
              <p className="text-sm text-gray-400 max-w-[400px] leading-relaxed">{organization.tagline}</p>
            </div>
            <p className="text-sm leading-relaxed">
              A 501(c)(3) nonprofit charitable organization serving our community.
            </p>
            <p className="text-xs text-gray-400">Tax ID: {organization.tax_id || organization.taxId}</p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="text-sm hover:text-blue-400 transition-colors duration-200">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/programs" className="text-sm hover:text-blue-400 transition-colors duration-200">
                  Our Services
                </Link>
              </li>
              <li>
                <Link to="/volunteer" className="text-sm hover:text-blue-400 transition-colors duration-200">
                  Volunteer
                </Link>
              </li>
              <li>
                <Link to="/donate" className="text-sm hover:text-blue-400 transition-colors duration-200">
                  Donate
                </Link>
              </li>
              <li>
                <Link to="/news" className="text-sm hover:text-blue-400 transition-colors duration-200">
                  News & Events
                </Link>
              </li>
              <li>
                <Link to="/events" className="text-sm hover:text-blue-400 transition-colors duration-200">
                  Conference 2027
                </Link>
              </li>
              <li>
                <Link to="/gallery" className="text-sm hover:text-blue-400 transition-colors duration-200">
                  Gallery
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{organization.address}</span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <span className="text-sm">{organization.phone}</span>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <span className="text-sm">{organization.email}</span>
              </li>
            </ul>
          </div>

          {/* Newsletter Signup */}
          <div>
            <h4 className="text-white font-semibold mb-4">Stay Connected</h4>
            <p className="text-sm mb-4">Subscribe to our newsletter for updates and stories of hope.</p>
            {subscribed ? (
              <div className="flex items-center gap-2 text-green-400 mb-4">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm">Thanks for subscribing!</span>
              </div>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="flex space-x-2 mb-4">
                <input
                  type="email"
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  data-testid="newsletter-email-input"
                />
                <button 
                  type="submit"
                  disabled={isSubscribing}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                  data-testid="newsletter-subscribe-btn"
                >
                  {isSubscribing ? '...' : 'Subscribe'}
                </button>
              </form>
            )}
            {/* Social Media - Link to main platforms (update in Admin when pages are created) */}
            <div className="flex space-x-3">
              <a
                href={organization.facebook_url || "https://facebook.com"}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors duration-200"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={organization.twitter_url || "https://x.com"}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-blue-400 rounded-full flex items-center justify-center transition-colors duration-200"
                data-testid="social-twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a
                href={organization.instagram_url || "https://instagram.com"}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-pink-600 rounded-full flex items-center justify-center transition-colors duration-200"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href={organization.linkedin_url || "https://linkedin.com"}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-blue-700 rounded-full flex items-center justify-center transition-colors duration-200"
                data-testid="social-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400">
              © {currentYear} {organization.name?.endsWith('.') ? organization.name.slice(0, -1) : organization.name}. All rights reserved. | 501(c)(3) Charitable Organization
            </p>
            <div className="flex items-center space-x-6">
              <a 
                href="https://app.emergent.sh/?utm_source=emergent-badge" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-gray-500 hover:text-gray-300 transition-colors duration-200 flex items-center"
              >
                Made with Emergent
              </a>
              <Link to="/privacy" className="text-sm text-gray-400 hover:text-blue-400 transition-colors duration-200">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-gray-400 hover:text-blue-400 transition-colors duration-200">
                Terms of Service
              </Link>
              <Link to="/admin/login" className="text-sm text-gray-500 hover:text-gray-300 transition-colors duration-200">
                Admin
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
